import type { CSSProperties, ReactNode } from 'react';

import { getQuadrant } from '@/utils/dom';

import type { TooltipInfo } from './types';

/** 커서 위치로부터의 오프셋 (px) */
const CURSOR_OFFSET = 12;

type ChartTooltipProps = {
  /** 툴팁 정보 — 위치 및 데이터 */
  info: TooltipInfo;
  /** 차트 컨테이너의 DOMRect — 오버플로 방지용 */
  containerRect: DOMRect;
  /** 툴팁 콘텐츠 render prop */
  children: (info: TooltipInfo) => ReactNode;
  /** tooltip에 마우스가 진입할 때 호출 — dismiss 타이머 취소용 */
  onMouseEnter?: () => void;
  /** tooltip에서 마우스가 떠날 때 호출 — dismiss 타이머 시작용 */
  onMouseLeave?: () => void;
};

/**
 * 캔버스 차트용 DOM 툴팁 오버레이.
 * 마우스 위치의 사분면에 따라 툴팁을 배치하여 오버플로를 방지한다.
 */
export const ChartTooltip = ({
  info,
  containerRect,
  children,
  onMouseEnter,
  onMouseLeave,
}: ChartTooltipProps) => {
  const quadrant = getQuadrant(
    { width: containerRect.width, height: containerRect.height },
    { top: info.anchorY ?? info.y, left: info.anchorX ?? info.x },
  );

  /** 사분면에 따른 동적 좌표 */
  const position = getTooltipPosition(quadrant, info, containerRect);

  return (
    <div
      className="absolute z-50 min-w-[160px] rounded-lg bg-white p-2 shadow-lg transition-opacity duration-150"
      style={position}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children(info)}
    </div>
  );
};

/**
 * 사분면에 따라 커서 반대편에 툴팁을 배치한다.
 *
 * - Q1 (우상) → 좌하에 배치
 * - Q2 (좌상) → 우하에 배치
 * - Q3 (좌하) → 우상에 배치
 * - Q4 (우하) → 좌상에 배치
 */
function getTooltipPosition(
  quadrant: 1 | 2 | 3 | 4,
  info: TooltipInfo,
  rect: DOMRect,
): CSSProperties {
  const ax = info.anchorX ?? info.x;
  const ay = info.anchorY ?? info.y;
  switch (quadrant) {
    case 1:
      return { right: rect.width - ax + CURSOR_OFFSET, top: ay + CURSOR_OFFSET };
    case 2:
      return { left: ax + CURSOR_OFFSET, top: ay + CURSOR_OFFSET };
    case 3:
      return { left: ax + CURSOR_OFFSET, bottom: rect.height - ay + CURSOR_OFFSET };
    case 4:
      return { right: rect.width - ax + CURSOR_OFFSET, bottom: rect.height - ay + CURSOR_OFFSET };
  }
}
